import React from 'react';
import Select, { MultiValue } from 'react-select';
import { RequestBody } from '../services/api';
import makes from '../utils/makes_cleaned.json';
import models from '../utils/models_cleaned.json';
import yearsData from '../utils/years_cleaned.json';
import bodyData from '../utils/body_cleaned.json';
import drivetrainData from '../utils/drivetrain_cleaned.json';

interface Option<T = any> {
    value: T;
    label: string;
}

type ModelsData = {
    [key: string]: string[];
}

const modelsData = models as ModelsData;
const years = yearsData.years;

// Flatten and deduplicate all models across makes so we can show "All models"
const allModelStrings: string[] = Array.from(new Set(Object.values(modelsData).flat()));
const allModels = allModelStrings.map(m => ({ value: m, label: m }));

// Year options for selects
const yearOptions: Option<number>[] = years.map((y: number) => ({ value: y, label: String(y) }));

// Body type options
const bodyOptions: Option<string>[] = (bodyData.body || []).map((b: string) => ({ value: b, label: b }));

// Drivetrain options
const drivetrainOptions: Option<string>[] = (drivetrainData.drivetrain || []).map((d: string) => ({ value: d, label: d }));

interface FilterSidebarProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    requestBody: RequestBody;
    setRequestBody: React.Dispatch<React.SetStateAction<RequestBody>>;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ isOpen, setIsOpen, requestBody, setRequestBody }) => {
    return (
        <aside className={`filters-sidebar ${isOpen ? 'open' : 'collapsed'}`}>
            <div className="filters-header">
                <h2>Filters</h2>
                <button
                    className="close-sidebar-btn"
                    onClick={() => setIsOpen(false)}
                    title="Hide Filters"
                >
                    ✕
                </button>
            </div>

            <div className="filter-form">
                <div className="filter-group">
                    <label>Makes</label>
                    <Select
                        isMulti
                        options={makes}
                        classNamePrefix="select"
                        value={makes.filter(make => requestBody.makes?.includes(make.label))}
                        onChange={(selected) => {
                            setRequestBody(prev => ({
                                ...prev,
                                makes: selected ? selected.map(option => option.label) : null,
                                model: null // Reset model when makes change
                            }));
                        }}
                    />
                </div>

                <div className="filter-group">
                    <label>Model</label>
                    <Select
                        isMulti
                        isClearable
                        classNamePrefix="select"
                        options={
                            !requestBody.makes || requestBody.makes.length === 0
                                ? allModels
                                : Array.from(new Set((requestBody.makes || [])
                                    .flatMap(m => modelsData[m.toUpperCase()] ?? [])))
                                    .map((model: string) => ({ value: model, label: model }))
                        }
                        value={requestBody.model ? requestBody.model.map(m => ({ value: m, label: m })) : null}
                        onChange={(selected: MultiValue<Option<string>> | null) => {
                            setRequestBody(prev => ({
                                ...prev,
                                model: selected ? selected.map(s => s.value) : null
                            }));
                        }}
                    />
                </div>

                <div className="filter-group">
                    <label>Year Range</label>
                    <div className="range-inputs">
                        <Select
                            isClearable
                            classNamePrefix="select"
                            options={yearOptions}
                            placeholder="From"
                            value={requestBody.yeargt ? { value: requestBody.yeargt, label: String(requestBody.yeargt) } : null}
                            onChange={(selected: Option<number> | null) => setRequestBody(prev => ({
                                ...prev,
                                yeargt: selected ? selected.value : null
                            }))}
                        />
                        <Select
                            isClearable
                            classNamePrefix="select"
                            options={yearOptions}
                            placeholder="To"
                            value={requestBody.yearlt ? { value: requestBody.yearlt, label: String(requestBody.yearlt) } : null}
                            onChange={(selected: Option<number> | null) => setRequestBody(prev => ({
                                ...prev,
                                yearlt: selected ? selected.value : null
                            }))}
                        />
                    </div>
                </div>

                <div className="filter-group">
                    <label>Body Type</label>
                    <Select
                        isMulti
                        isClearable
                        classNamePrefix="select"
                        options={bodyOptions}
                        value={requestBody.body ? requestBody.body.map(b => ({ value: b, label: b })) : null}
                        onChange={(selected: MultiValue<Option<string>> | null) => setRequestBody(prev => ({
                            ...prev,
                            body: selected && selected.length > 0 ? selected.map(s => s.value) : null
                        }))}
                    />
                </div>

                <div className="filter-group">
                    <label>Drivetrain</label>
                    <Select
                        isMulti
                        isClearable
                        classNamePrefix="select"
                        options={drivetrainOptions}
                        value={requestBody.drivetrain ? requestBody.drivetrain.map(d => ({ value: d, label: d })) : null}
                        onChange={(selected: MultiValue<Option<string>> | null) => setRequestBody(prev => ({
                            ...prev,
                            drivetrain: selected && selected.length > 0 ? selected.map(s => s.value) : null
                        }))}
                    />
                </div>

                <div className="filter-group">
                    <label>Average Insurance</label>
                    <div className="range-inputs">
                        <input
                            type="number"
                            placeholder="Min"
                            value={requestBody.averagegt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                averagegt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                        <input
                            type="number"
                            placeholder="Max"
                            value={requestBody.averagelt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                averagelt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                    </div>
                </div>

                <div className="filter-group">
                    <label>Collision Insurance</label>
                    <div className="range-inputs">
                        <input
                            type="number"
                            placeholder="Min"
                            value={requestBody.collisiongt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                collisiongt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                        <input
                            type="number"
                            placeholder="Max"
                            value={requestBody.collisionlt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                collisionlt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                    </div>
                </div>

                <div className="filter-group">
                    <label>Comprehensive Insurance</label>
                    <div className="range-inputs">
                        <input
                            type="number"
                            placeholder="Min"
                            value={requestBody.compgt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                compgt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                        <input
                            type="number"
                            placeholder="Max"
                            value={requestBody.complt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                complt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                    </div>
                </div>

                <div className="filter-group">
                    <label>Direct Compensation</label>
                    <div className="range-inputs">
                        <input
                            type="number"
                            placeholder="Min"
                            value={requestBody.dcpdgt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                dcpdgt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                        <input
                            type="number"
                            placeholder="Max"
                            value={requestBody.dcpdlt || ''}
                            onChange={(e) => setRequestBody(prev => ({
                                ...prev,
                                dcpdlt: e.target.value ? parseInt(e.target.value) : null
                            }))}
                        />
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default FilterSidebar;
